<template>
  <v-container fluid pa-0>
    <v-layout :column="column">
      <v-flex :class="{ 'pl-3': !column }">
        <v-menu
          :close-on-content-click="autoclose"
          :nudge-right="40"
          :disabled="disabled"
          full-width
          lazy
          min-width="290px"
          offset-y
          ref="menu"
          transition="scale-transition"
          v-model="menu"
        >
          <v-text-field
            :label="label"
            :required="required"
            @blur="dateInputFocused = false"
            @focus="dateInputFocused = true"
            @change="onDateInsert"
            :disabled="disabled"
            :prepend-icon="column ? null : 'event'"
            :append-icon="column ? 'event' : null"
            :value="displayDate"
            :rules="[rules.min, rules.max]"
            :hint="$t('message.form.fields.scheduleDateHint')"
            mask="##/##/####"
            slot="activator"
          ></v-text-field>
          <v-date-picker
            :allowedDates="allowedDates"
            :first-day-of-week="1"
            :locale="locale"
            :disabled="disabled"
            :title-date-format="titleDateFormat"
            :value="dateValue"
            @input="onDateSelected"
            no-title
            scrollable>
            <v-spacer></v-spacer>
            <v-btn flat color="primary" @click="menu = false" v-if="!hideButtons">
              {{ $t('message.common.cancel') }}
            </v-btn>
            <v-btn flat color="primary" @click="$refs.menu.save(value)" v-if="!hideButtons">
              {{ $t('message.common.accept') }}
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex :class="{ 'pl-3': !column }">
        <v-menu
          :close-on-content-click="false"
          :disabled="disabled"
          :nudge-right="40"
          full-width
          lazy
          max-width="290px"
          min-width="290px"
          offset-y
          transition="slide-y-reverse-transition"
          v-model="timeMenu"
        >
          <v-text-field
            :label="timeLabel"
            :disabled="disabled"
            :required="required"
            :value="pickerTime"
            @blur="timeInputFocused = false"
            @focus="timeInputFocused = true"
            @change="onTimeInsert"
            :hint="$t('message.form.fields.scheduleTimeHint')"
            :error="invalidRules"
            mask="##:##"
            append-icon="access_time"
            slot="activator"
          ></v-text-field>
          <v-time-picker
            :disabled="disabled"
            :min="minTime"
            :max="maxTime"
            :locale="locale"
            :value="pickerTime"
            @input="onTimeSelected"
            format="24hr"
          ></v-time-picker>
        </v-menu>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import moment from 'moment';
import { pickBy, isEmpty } from 'lodash';
import DatePicker from './DatePicker';

export default {
  name: 'nx-date-time-picker',
  extends: DatePicker,
  data() {
    return {
      timeMenu: false,
      timeInputFocused: false,
    };
  },

  props: {
    column: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    timeLabel: {
      type: String,
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    minRule() {
      if (this.minDate) {
        return moment(this.minDate).isBefore(this.value) || `${this.$t('message.form.fields.scheduleDateRuleAfter')} ${moment(this.minDate).format('DD/MM/YYYY HH:mm')}`;
      }
      return true;
    },
    maxRule() {
      if (this.maxDate) {
        return moment(this.maxDate).isAfter(this.value) || `${this.$t('message.form.fields.scheduleDateRuleBefore')}  ${moment(this.maxDate).format('DD/MM/YYYY HH:mm')}`;
      }
      return true;
    },
    rules() {
      return {
        min: this.minRule,
        max: this.maxRule,
      };
    },
    invalidRules() {
      return !isEmpty(pickBy(this.rules, value => value !== true));
    },
    pickerTime() {
      if (!this.value) return null;
      return moment(this.value).format('HH:mm');
    },
    dateValue() {
      if (!this.value) return null;
      return moment(this.value).format('YYYY-MM-DD');
    },
    max() {
      if (!this.maxDate) {
        return null;
      }
      return moment(this.maxDate).toISOString();
    },
    min() {
      if (!this.minDate) {
        return null;
      }
      return moment(this.minDate).toISOString();
    },
    maxTime() {
      if (!this.max) {
        return null;
      }
      if (this.value
        && moment(this.value).format('L') === moment(this.max).format('L')) {
        return moment(this.maxDate).format('hh:mm');
      }
      return null;
    },
    minTime() {
      if (!this.min) {
        return null;
      }
      if (this.value
        && moment(this.value).format('L') === moment(this.min).format('L')) {
        return moment(this.minDate).format('hh:mm');
      }
      return null;
    },
    focused() {
      return this.dateInputFocused || this.menu
        || this.timeInputFocused || this.timeMenu;
    },
  },

  methods: {
    onDateInsert(newValue) {
      if (newValue.length === 8 || null) {
        const newDate = `${newValue.slice(4, 8)}-${newValue.slice(2, 4)}-${newValue.slice(0, 2)}`;
        this.onDateSelected(newDate);
      }
    },
    onDateSelected(newValue) {
      if (newValue === null) {
        this.$emit('input', null);
        return;
      }
      const value = this.value ? moment(this.value) : moment();
      const newDate = moment(newValue, 'YYYY-MM-DD');
      value.set('date', newDate.date());
      value.set('month', newDate.month());
      value.set('year', newDate.year());
      this.$emit('input', value.format());
    },
    onTimeInsert(newValue) {
      if (newValue.length === 4 || null) {
        const newTime = `${newValue.slice(0, 2)}:${newValue.slice(2, 4)}`;
        this.onTimeSelected(newTime);
      }
    },
    onTimeSelected(newValue) {
      if (newValue === null) {
        this.$emit('input', null);
        return;
      }
      const value = this.value ? moment(this.value) : moment();
      const [hour, minutes] = newValue.split(':');
      if (hour) {
        value.hours(hour);
      }
      if (minutes) {
        value.minutes(minutes);
      }
      this.$emit('input', value.format());
    },
  },
};
</script>
